import NumberFlow from "@number-flow/react";
import { cn, ControlCategories, pluralize } from "@theprelude/core";
import {
  AlertIcon,
  CheckmarkIcon,
  ExceptionIcon,
  ResourceTypeIcon,
} from "@theprelude/ds";
import { DataPoint } from "@/queries/control-monitor";
import { NodeBase, NodeBaseProps } from "./node-base";

interface AggregateNodeProps
  extends Omit<NodeBaseProps, "nodeId" | "name" | "children" | "hasChildren"> {
  data: DataPoint;
}

export const AggregateNode = ({ data, ...props }: AggregateNodeProps) => {
  const issueCount = data.controlFailureCount + data.misconfigurationCount;

  return (
    <NodeBase
      nodeId={data.id}
      name={data.name}
      disabled={!data.enabled}
      hasChildren={data.children.length > 0}
      {...props}
    >
      <div className="flex h-6 w-full items-center justify-between gap-2 border-t border-stroke-line px-2 py-1">
        <div className="flex items-center gap-2 text-xs font-semibold">
          <span className="flex items-center gap-1 overflow-hidden whitespace-nowrap">
            <ResourceTypeIcon resourceType={data.resourceType} />
            <NumberFlow value={data.resourceCount} />
          </span>
          {showIssueCount(data) && (
            <span
              className={cn(
                "flex items-center gap-1 overflow-hidden whitespace-nowrap transition-colors",
                data.enabled
                  ? issueCount > 0
                    ? "text-status-red"
                    : "text-status-green"
                  : undefined,
              )}
            >
              {issueCount > 0 ? <AlertIcon /> : <CheckmarkIcon />}
              <NumberFlow value={issueCount} /> {pluralize(issueCount, "issue")}
            </span>
          )}
          {data.exceptionCount > 0 && (
            <span className="flex items-center gap-1 overflow-hidden whitespace-nowrap transition-colors">
              <ExceptionIcon />
              <span className="inline-flex items-center justify-center rounded-full bg-tertiary-background px-2 py-0 text-[0.625rem] text-white font-normal">
                <NumberFlow value={data.exceptionCount} />
              </span>
            </span>
          )}
        </div>
      </div>
    </NodeBase>
  );
};

const showIssueCount = (data: DataPoint) => {
  if (
    data.type === "connected-control" &&
    data.category === ControlCategories.DISCOVERED_DEVICES
  ) {
    return false;
  }
  if (data.type === "control-category" && data.id === "discovered-devices") {
    return false;
  }
  return true;
};
